import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../admin/AdminDashboard.css";
import "./AdminAdvertisers.css";

import { db, storage } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const AVAILABLE_PAGES = [
  { id: "home", label: "Home" },
  { id: "trails", label: "Trails List" },
  { id: "login", label: "Login" },
  { id: "register", label: "Register" },
  { id: "dashboard", label: "Dashboard" },
  { id: "startTrail", label: "Start Trail" },
];

export default function AdminAdvertiserModify() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [advertiser, setAdvertiser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Editable fields
  const [name, setName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);

  // Placement fields
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectedTrails, setSelectedTrails] = useState([]);
  const [availableTrails, setAvailableTrails] = useState([]);

  /* ------------------------------------------------------------
     Load advertiser + trail list
  ------------------------------------------------------------ */
  useEffect(() => {
    async function loadAll() {
      try {
        // Load advertiser
        const refA = doc(db, "advertisers", id);
        const snap = await getDoc(refA);

        if (snap.exists()) {
          const data = snap.data();
          setAdvertiser({ id: snap.id, ...data });

          setName(data.name || "");
          setLinkUrl(data.linkUrl || "");
          setSelectedPages(data.showOnPages || []);
          setSelectedTrails(data.showOnTrails || []);
        } else {
          setError("Advertiser not found.");
        }

        // Load trails list
        const trailsSnap = await getDocs(collection(db, "trails"));
        const trailsList = trailsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setAvailableTrails(trailsList);
      } catch (err) {
        console.error(err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    }

    loadAll();
  }, [id]);

  /* ------------------------------------------------------------
     Toggle page / trail checkboxes
  ------------------------------------------------------------ */
  const togglePage = (pageId) => {
    setSelectedPages((prev) =>
      prev.includes(pageId)
        ? prev.filter((p) => p !== pageId)
        : [...prev, pageId]
    );
  };

  const toggleTrail = (trailId) => {
    setSelectedTrails((prev) =>
      prev.includes(trailId)
        ? prev.filter((t) => t !== trailId)
        : [...prev, trailId]
    );
  };

  /* ------------------------------------------------------------
     Save advertiser changes
  ------------------------------------------------------------ */
  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    try {
      setSaving(true);

      let newImageUrl = advertiser.imageUrl;

      if (imageFile) {
        const imgRef = ref(
          storage,
          `advertisers/${Date.now()}_${imageFile.name}`
        );
        await uploadBytes(imgRef, imageFile);
        newImageUrl = await getDownloadURL(imgRef);
      }

      await updateDoc(doc(db, "advertisers", id), {
        name,
        linkUrl,
        imageUrl: newImageUrl,
        showOnPages: selectedPages,
        showOnTrails: selectedTrails,
      });

      navigate("/admin/advertisers");
    } catch (err) {
      console.error("Error saving advertiser:", err);
      setError("Failed to save changes.");
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------
     RENDER
  ------------------------------------------------------------ */
  if (loading) {
    return (
      <div className="admin-page">
        <h1>Modify Advertiser</h1>
        <p>Loading…</p>
      </div>
    );
  }

  if (!advertiser) {
    return (
      <div className="admin-page">
        <h1>Modify Advertiser</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <h1>Modify Advertiser</h1>
      <p className="admin-subtitle">Update advertiser info & placement</p>

      <div className="section-box">
        <form className="admin-form-large" onSubmit={handleSave}>
          {error && <p style={{ color: "#ff8fb4" }}>{error}</p>}

          {/* BASIC FIELDS */}
          <label className="admin-label">Name</label>
          <input
            type="text"
            className="admin-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <label className="admin-label">Link URL</label>
          <input
            type="url"
            className="admin-input"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />

          <label className="admin-label">Replace Banner (optional)</label>
          <input
            type="file"
            className="admin-input"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0] || null)}
          />

          <div style={{ textAlign: "center", marginTop: "14px" }}>
            <img
              src={advertiser.imageUrl}
              alt="Current Banner"
              className="advertiser-thumb"
              style={{ width: "140px", height: "auto" }}
            />
            <p style={{ color: "#aaa", marginTop: "4px" }}>Current Banner</p>
          </div>

          {/* PAGE SELECTION */}
          <div style={{ marginTop: "20px" }}>
            <label className="admin-label">Show on Pages</label>
            <div
              style={{
                marginTop: "8px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "6px",
              }}
            >
              {AVAILABLE_PAGES.map((page) => (
                <label key={page.id} style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={selectedPages.includes(page.id)}
                    onChange={() => togglePage(page.id)}
                    style={{ marginRight: "8px" }}
                  />
                  {page.label}
                </label>
              ))}
            </div>
          </div>

          {/* TRAIL SELECTION */}
          <div style={{ marginTop: "25px" }}>
            <label className="admin-label">Show on Trails</label>

            <div
              style={{
                maxHeight: "240px",
                overflowY: "auto",
                background: "rgba(255,255,255,0.06)",
                borderRadius: "8px",
                padding: "10px",
                marginTop: "8px",
              }}
            >
              {availableTrails.map((trail) => (
                <label
                  key={trail.id}
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedTrails.includes(trail.id)}
                    onChange={() => toggleTrail(trail.id)}
                    style={{ marginRight: "8px" }}
                  />
                  {trail.name || trail.id}
                </label>
              ))}
            </div>
          </div>

          {/* BUTTONS */}
          <button className="admin-button" type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>

          <button
            type="button"
            className="admin-button secondary"
            onClick={() => navigate("/admin/advertisers")}
            style={{ marginTop: "10px" }}
          >
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
}
